<template>
  <div class="container">
    <div class="panel">
      <div
        class="app-alert-error reservation-err-msg"
        id="appAlert"
        role="alert"
        ref="appAlert"
        v-if="errMsgForReservation"
      >
        <img src="@ridmv/web-themes/images/icons/exclamation-triangle-solid.svg" alt="" />
        <div class="res-err">
          <p>
            You can have only one reservation at a time. If needed, modify / cancel your upcoming
            reservations listed below.
          </p>
          <div>
            <button class="btn-close" type="button" @click="closeBanner()" @keydown.enter="closeBanner()" aria-label="Close">X</button>
          </div>
        </div>
      </div>

      <div class="tab" tabindex="0" @keypress.enter="toggleAccordion('personalTab')" role="tab">
        <input type="radio" value="personalTab" id="accordion-header-1" name="personalTab"
          v-model="activeTab" @click="toggleAccordion('personalTab')" aria-controls="personalInfo"/>
        <div class="tab-header">
          <label class="tab-label tabtitle" for="accordion-header-1">
            <span class="tab-title-text">{{ isBusinessUser ? 'Profile' : 'Profile & Credentials' }}</span>
            <div class="tab-actions">
            <Tooltip theme="my-custom-tooltip" :triggers="['hover', 'click', 'focus']">
              <button type="button" class="btn-tooltip-trigger refresh-btn" aria-label="Refresh Profile & Credentials Tab" @click="refreshTab('personalTab')">
                <img
                  src="/static/refreshIcon.png"
                  alt="Refresh Profile & Credentials Tab" 
                />
              </button>
              <span class="sr-only"></span>
              <template #popper>
                Refresh
              </template>
            </Tooltip>
            <span class="tab-toggle-icon"></span>
            </div>
          </label>
        </div>
        <div class="tab-content">
          <template v-if="isBusinessUser">
            <BusinessInfo @doRefresh="doRefresh" />
          </template>
          <template v-else>
            <PersonalInfo @doRefresh="doRefresh" />
            <Credentials />
          </template>     
        </div>
      </div>

      <div class="tab" tabindex="0" @keypress.enter="toggleAccordion('registrationTab')" role="tab">
      <input type="radio" value="registrationTab" id="accordion-header-2"
        name="registrationTab" v-model="activeTab" @click="toggleAccordion('registrationTab')"/>
      <div class="tab-header">
        <label class="tab-label" for="accordion-header-2">
          <span class="tab-title-text">Registration</span>
          <div class="tab-actions">
          <Tooltip theme="my-custom-tooltip" :triggers="['hover', 'click', 'focus']">
            <button type="button" class="btn-tooltip-trigger refresh-btn" aria-label="Refresh Registration Tab" @click="refreshTab('registrationTab')">
              <img
                src="/static/refreshIcon.png"
                alt="Refresh Registration Tab" 
              />
            </button>
            <span class="sr-only"></span>
            <template #popper>
              Refresh
            </template>
          </Tooltip>
          <span class="tab-toggle-icon"></span>
          </div>
        </label>    
      </div>
      <div class="tab-content">
        <Registration ref="registrationRef" />
      </div>
      </div>
      <div
        class="tab"
        tabindex="0"
        @keypress.enter="toggleAccordion('adjudicationTab')"
        role="tab"
      >
        <input
          type="radio"
          value="adjudicationTab"
          id="accordion-header-3"
          name="adjudicationTab"
          v-model="activeTab"
          @click="toggleAccordion('adjudicationTab')"
        />
        <div class="tab-header">
          <label class="tab-label" for="accordion-header-3">
            <span class="tab-title-text">Adjudication</span>
            <div class="tab-actions">
              <Tooltip theme="my-custom-tooltip" :triggers="['hover', 'click', 'focus']">
                <button type="button" class="btn-tooltip-trigger refresh-btn" aria-label="Refresh Adjudication Tab" @click="refreshTab('adjudicationTab')">
                  <img
                    src="/static/refreshIcon.png"
                    alt="Refresh Adjudication Tab" 
                  />
                </button>
                <span class="sr-only"></span>
                <template #popper>
                  Refresh
                </template>
              </Tooltip>
              <span class="tab-toggle-icon"></span>
            </div>
          </label>
        </div>
        <div class="tab-content">
          <Adjudication />
        </div>
      </div>
    </div>

    <AsideForLinks @doCheckReservation="checkReservation" />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useStore } from 'vuex';
import * as COMMONS from '../../store/_commons';
// Individual components
import PersonalInfo from '../profile/PersonalInfo.vue';
import Credentials from '../profile/Credentials.vue';
import Registration from '../profile/Registration.vue';
import Adjudication from '../profile/Adjudication.vue';
// Business components
import BusinessInfo from '../business/profile/BusinessInfo.vue';
import AsideForLinks from '../confirmation/AsideForLinks.vue';
import { Tooltip } from 'floating-vue';
import '@/assets/tooltip.css';

// Vuex store
const store = useStore();

// Computed property to check if user is business
const isBusinessUser = computed(() => store.state.guestLogin.isBusinessUser);


// Reactive state
const activeTab = ref('personalTab');
const personalTabCount = ref(0);
const registrationTabCount = ref(0);
const adjudicationTabCount = ref(0);
const errMsgForReservation = ref(false);

const registrationRef = ref(null);

// Methods
const toggleAccordion = (tabName) => {
  window.scrollTo(0, 0);

  // switching away from Registration tab
  if (activeTab.value === 'registrationTab' && tabName !== 'registrationTab') {
    // clear search results
    registrationRef.value?.resetSearchSection?.();
  }

  activeTab.value = activeTab.value === tabName ? null : tabName;
  
  if (tabName == 'registrationTab') {
    getRegistrationInformationData();
  }
  if (tabName == 'adjudicationTab') {
    getAdjudicationInformationData();
  }
};

const getRegistrationInformationData = () => {
  if (registrationTabCount.value == 0) {
     // Dispatch correct action based on user type
    if (isBusinessUser.value) {
      store.dispatch(COMMONS.BUSINESS_REGISTRATION);
    } else {
      store.dispatch(COMMONS.GUEST_REGISTRATION);
    }
    registrationTabCount.value = 1;
  }
};

const getAdjudicationInformationData = () => {
  if (adjudicationTabCount.value == 0) {
    if (isBusinessUser.value) {
      store.dispatch(COMMONS.GUEST_BUSINESS_ADJUDICATION);
    } else {
      store.dispatch(COMMONS.GUEST_ADJUDICATION);
    }
    adjudicationTabCount.value = 1;
  }
};

const checkReservation = (val) => {
  errMsgForReservation.value = val;
};

const closeBanner = () => {
  errMsgForReservation.value = false;
};

const doRefresh = () => {
  closeBanner();
};

const refreshTab = (tabName) => {
  switch (tabName) {
    case 'personalTab':
      if (isBusinessUser.value) {
        store.dispatch(COMMONS.BUSINESS_PROFILE_GET_PROFILE);
      } else {
        store.dispatch(COMMONS.GUEST_PROFILE);
      } 
      break;

    case 'registrationTab':
      registrationRef.value?.resetSearchSection?.();
      registrationTabCount.value = 0;          // reset so toggleAccordion can call again
      getRegistrationInformationData();        
      break;

    case 'adjudicationTab':
      adjudicationTabCount.value = 0;
      getAdjudicationInformationData();
      break;
  }
};

// Lifecycle hooks
onMounted(() => {
  window.scrollTo(0, 0);
});

onBeforeUnmount(() => {
  personalTabCount.value = 0;
  registrationTabCount.value = 0;
  adjudicationTabCount.value = 0;
});
</script>

<style scoped lang="scss">
.container {
  display: flex;
  gap: 2rem;
  position: relative;
}
input {
  position: absolute;
  opacity: 0;
  z-index: -1;
}
/* Accordion styles */
.panel {
  overflow: hidden;
  width: 80%;
  .reservation-err-msg {
    position: relative;
    margin-bottom: 10px;
    .res-err{ 
      .btn-close {
        position: absolute;
        right: 0;
        top: 0;
        min-width: 40px;
        max-width: 40px;
        border: 0px;
        background-color: transparent;
        color: black;
      }
    }
  }
}
.tab {
  width: 100%;
  overflow: hidden;
  &:focus {
    outline-color:orange;
  }
  &-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1em;
    background: var(--ri-light-blue);
    font-weight: bold;
    cursor: pointer;
    margin-bottom: 0px;
    width: 100%;
    /* Icon */
    &:hover {
      background: orange;
    }
    .btn-tooltip-trigger{
      min-width: 25px;
      border: none;
      background-color: unset;
      border-radius: unset;
      padding: 0rem;
    }
  }
  &-content {
    max-height: 0;
    display: none;
    padding: 0 1em;
    color: black;
    transition: all .35s;
    border: 1px solid  var(--ri-gray);
  }
  .tabtitle{
    margin-top: 0rem;
    .tab-title-text {
      margin-right: 0.5rem;   // spacing between text and refresh icon
    }
  }
  .tab-actions {
    display: flex;
    align-items: center;
    gap: 1rem;   
  }
  .tab-toggle-icon {
    font-size: 1.5rem;
    font-weight: bold;
    width: 1rem;
    text-align: center;
  }
  .tab-toggle-icon::after {
    content: "+";
    font-size: 1.5rem;
    font-weight: bold;
    display: inline-block;
    width: 1rem;
    text-align: center;
  }
  /* When input is NOT checked → show + */
  input + .tab-label .tab-actions .tab-toggle-icon {
    content: "+";
  }
  /* When input IS checked → show - */
  input:checked + .tab-label .tab-actions .tab-toggle-icon {
    content: "-";
  }
  input:checked ~ .tab-header .tab-toggle-icon::after {
   content: "-";
  }
}

input:checked {
  + .tab-label {
    &::after {
      content: "-";
    }
  }
  ~ .tab-content {
    max-height: 100%;
    display: block;
    padding: 1em;
  }
}

.right-panel{
  overflow: hidden;
  margin-top: 16px;
  .tab-right {
    .tab-label-text{
      background: var(--ri-light-blue);
      display: flex;
      justify-content: space-between;
      padding: 1em;
      font-weight: bold;
      cursor: pointer;
      &::after {
        content: none; 
      }
    }
  }
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.refresh-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  color: black;
  img {
    width: 25px;
    margin-top: 7px;
  }
}

@media only screen and (max-width: 1024px) {
  .panel{
    width: 100%;
  }
  .container {
    display: block;
    gap: 2rem;
  }
  .right-panel{
    overflow: hidden;
    margin-top: 16px;
    width: 100%;
    .tab-right {
      .tab-label-text{
        background: var(--ri-light-blue);
        display: flex;
        justify-content: space-between;
        padding: 1em;
        font-weight: bold;
        cursor: pointer;
        &::after {
          content: none; 
        }
      }
    }
  }
}
</style> 